import _Accordion from "@mui/material/Accordion";
import _AccordionDetails from "@mui/material/AccordionDetails";
import _AccordionSummary from "@mui/material/AccordionSummary";
import _ChevronRight from "@mui/icons-material/ChevronRight";
import _Box from "@mui/material/Box";
import _Typography from "@mui/material/Typography";
import React from 'react';
import { useTranslation } from 'react-i18next';
import communicationIllustration from '../../assets/images/communication-illus.svg';
import { useAmplitudeTracker } from '../../hooks/tracker';
import { Button } from '../button';
import BfgPointsRules from './bfg-points-rules';
import BfgScoreFormula from './bfg-score-formula';
function FaqChevron() {
  return /*#__PURE__*/React.createElement("svg", {
    fill: "none",
    height: "9",
    viewBox: "0 0 16 9",
    width: "16",
    xmlns: "http://www.w3.org/2000/svg"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M1 1L8 8L15 1",
    stroke: "#326AC2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    strokeWidth: "2"
  }));
}
function BfgCommKitBanner({
  communicationKitUrl
}) {
  const {
    t
  } = useTranslation();
  const {
    trackEvent
  } = useAmplitudeTracker();
  return /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    borderRadius: 4,
    display: "flex",
    gap: 2,
    justifyContent: "space-between",
    padding: 3,
    sx: {
      background: 'rgba(201, 233, 248, 0.50)'
    }
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 2
  }, /*#__PURE__*/React.createElement("img", {
    src: communicationIllustration,
    width: 48
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 0.5
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 14,
      fontWeight: 700
    }
  }, t('commons.challenges.bfg_faq.comm_kit.title')), /*#__PURE__*/React.createElement(_Typography, {
    color: "textSecondary",
    sx: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 1.5
    }
  }, t('commons.challenges.bfg_faq.comm_kit.description')))), communicationKitUrl && /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    endIcon: /*#__PURE__*/React.createElement(_ChevronRight, null),
    href: communicationKitUrl,
    onClick: () => trackEvent('Communication Kit Link Clicked', {
      context: 'BFG FAQ Tab'
    }),
    rel: "noreferrer",
    sx: {
      flexShrink: 0,
      fontSize: 14,
      fontWeight: 600,
      lineHeight: '22px'
    },
    target: "_blank",
    variant: "text"
  }, t('commons.challenges.bfg_faq.comm_kit.action')));
}
function BfgFaqAccordion() {
  const {
    t
  } = useTranslation();
  const faqs = [{
    question: t('commons.challenges.bfg_faq.faq_questions.score_question'),
    answer: t('commons.challenges.bfg_faq.faq_questions.score_answer')
  }, {
    question: t('commons.challenges.bfg_faq.faq_questions.zone_question'),
    answer: t('commons.challenges.bfg_faq.faq_questions.zone_answer')
  }, {
    question: t('commons.challenges.bfg_faq.faq_questions.velotaf_question'),
    answer: t('commons.challenges.bfg_faq.faq_questions.velotaf_answer')
  }, {
    question: t('commons.challenges.bfg_faq.faq_questions.lots_question'),
    answer: t('commons.challenges.bfg_faq.faq_questions.lots_answer')
  }, {
    question: t('commons.challenges.bfg_faq.faq_questions.deadline_question'),
    answer: t('commons.challenges.bfg_faq.faq_questions.deadline_answer')
  }, {
    question: t('commons.challenges.bfg_faq.faq_questions.goodplanet_impact_question'),
    answer: t('commons.challenges.bfg_faq.faq_questions.goodplanet_impact_answer')
  }];
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 18,
      fontWeight: 800
    }
  }, t('commons.challenges.bfg_faq.faq_title')), faqs.map(({
    question,
    answer
  }) => /*#__PURE__*/React.createElement(_Accordion, {
    disableGutters: true,
    elevation: 0,
    key: question,
    sx: {
      '&:before': {
        display: 'none'
      },
      background: '#FFF',
      border: '1px solid #9CBDF4',
      borderRadius: '8px !important',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement(_AccordionSummary, {
    expandIcon: /*#__PURE__*/React.createElement(FaqChevron, null),
    sx: {
      padding: 2
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 18,
      fontWeight: 800
    }
  }, question)), /*#__PURE__*/React.createElement(_AccordionDetails, {
    sx: {
      padding: 2,
      paddingTop: 0
    }
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.6
    }
  }, answer)))));
}
function BfgFAQ({
  communicationKitUrl,
  isAdminOrAnimator
}) {
  const {
    t
  } = useTranslation();
  return /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: "40px"
  }, /*#__PURE__*/React.createElement(_Box, {
    paddingTop: "40px"
  }, isAdminOrAnimator && /*#__PURE__*/React.createElement(BfgCommKitBanner, {
    communicationKitUrl: communicationKitUrl
  })), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    paddingX: "32px",
    paddingY: "32px"
  }, /*#__PURE__*/React.createElement(_Typography, {
    sx: {
      fontSize: 18,
      fontWeight: 800
    }
  }, t('commons.challenges.bttf_faq.title')), /*#__PURE__*/React.createElement(BfgPointsRules, null)), /*#__PURE__*/React.createElement(_Box, {
    paddingX: "32px",
    paddingY: "32px"
  }, /*#__PURE__*/React.createElement(BfgScoreFormula, null)), /*#__PURE__*/React.createElement(_Box, {
    paddingX: "32px",
    paddingY: "32px"
  }, /*#__PURE__*/React.createElement(BfgFaqAccordion, null)));
}
export default BfgFAQ;
export { BfgCommKitBanner, BfgFaqAccordion };